import { GridPagerSettings } from '@progress/kendo-react-grid';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import CardGridWrapper from 'src/components/CardGridWrapper';
import { useContextSelector } from 'use-context-selector';
import SearchCardGridHeader from './SearchCardGridHeader';
import SearchPictureViewCard from './SearchPictureViewCard';
import SearchProductInformationPanel from './SearchProductInformationPanel';
import { getEffectiveRefineSearch } from './searchProductContext.utils';
import { SearchProductDetailsContext } from './SearchProductDetailsContext';
import { SearchProductFiltersContext } from './SearchProductFiltersContext';
import { SearchProductQueryContext } from './SearchProductQueryContext';
import { SearchProductSelectionsContext } from './SearchProductSelectionsContext';
import { SearchResultProduct } from './searchProductData';

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 25, 50, 100],
};

const SearchProductPictureView: FC = () => {
  const { t } = useTranslation();

  const dataState = useContextSelector(SearchProductFiltersContext, (state) => state.dataState);
  const onDataStateChange = useContextSelector(
    SearchProductSelectionsContext,
    (state) => state.onDataStateChange
  );
  const loadingProducts = useContextSelector(
    SearchProductQueryContext,
    (state) => state.loadingProducts
  );
  const isPrimarySearchPending = useContextSelector(
    SearchProductQueryContext,
    (state) => state.isPrimarySearchPending
  );
  const isUpdatingCart = useContextSelector(
    SearchProductSelectionsContext,
    (state) => state.isUpdatingCart
  );
  const isSearchProductsError = useContextSelector(
    SearchProductQueryContext,
    (state) => state.isSearchProductsError
  );
  const getProductSelection = useContextSelector(
    SearchProductSelectionsContext,
    (state) => state.getProductSelection
  );
  const updateProductSelection = useContextSelector(
    SearchProductSelectionsContext,
    (state) => state.updateProductSelection
  );
  const productsData = useContextSelector(SearchProductQueryContext, (state) => state.products);
  const productsTotal = useContextSelector(SearchProductQueryContext, (state) => state.productsTotal);
  const isProductPanelOpen = useContextSelector(
    SearchProductDetailsContext,
    (state) => state.isProductPanelOpen
  );
  const handleCloseProductPanel = useContextSelector(
    SearchProductDetailsContext,
    (state) => state.handleCloseProductPanel
  );
  const refineSearch = useContextSelector(SearchProductFiltersContext, (state) => state.refineSearch);

  const hasRefineSearchValue = Boolean(getEffectiveRefineSearch(refineSearch));

  const renderCard = (item: SearchResultProduct) => {
    const { selected, qty } = getProductSelection(item.productKey);

    return (
      <SearchPictureViewCard
        key={item.productKey}
        product={item}
        selected={selected}
        onSelect={() => {
          updateProductSelection(item.productKey, {
            selected: !selected,
            qty: !selected && qty === 0 ? 1 : !selected ? qty : 0,
          });
        }}
        onQuantityChange={(newQty: number) => {
          updateProductSelection(item.productKey, {
            qty: newQty,
            selected: newQty !== 0,
          });
        }}
        quantity={qty}
      />
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {isSearchProductsError ? (
        <div className="mb-2 shrink-0 px-4 text-sm text-red-600">{t('searchResultsLoadFailed')}</div>
      ) : null}
      {hasRefineSearchValue && (
        <div className="mb-2 shrink-0 truncate px-4 text-sm font-[600]">
          {t('Showing results for')} &quot;{refineSearch.trim()}&quot;
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">
        <CardGridWrapper<SearchResultProduct>
          key={`search-products-${dataState.skip}-${dataState.take}`}
          dataSource={productsData ?? []}
          renderItem={renderCard}
          dataState={dataState}
          onDataStateChange={onDataStateChange}
          pageable={gridPagerSettings}
          loading={loadingProducts || isPrimarySearchPending || isUpdatingCart}
          className="min-h-0 flex-1"
          tableTextId="Items"
          stickyPager
          gap="gap-x-3 gap-y-2.5"
          height="100%"
          total={productsTotal}
          headerChildren={<SearchCardGridHeader />}
        />
      </div>
      <SearchProductInformationPanel
        isOpen={isProductPanelOpen}
        onClose={handleCloseProductPanel}
      />
    </div>
  );
};

export default SearchProductPictureView;
